import React, { useState, forwardRef, useImperativeHandle, useRef } from 'react';
import styled from 'styled-components';
import { Modal } from 'antd';
import { IAccountPackage, ICustomer } from 'features/customer/services/types/api';
import CreateOrUpdateCustomerForm from './CreateOrUpdateCustomerForm';
import customerApis from 'features/customer/services/apis';
import { IResponseDataBody } from 'services/response';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import { CustomerInfoPopover } from 'features/user/widgets/WaitingList/WalkIn/components/CustomerName';

type CreateOrUpdateCustomerProps = {
  onSuccess?: (data?: ICustomer) => void;
};

export type CreateOrUpdateCustomerRef = {
  onOpen: (data?: ICustomer) => void;
  openWithId: (id: string, configs?: { addon?: CustomerInfoPopover, onSuccess?: (data?: ICustomer) => void }) => void;
};

const CreateOrUpdateCustomer = forwardRef<CreateOrUpdateCustomerRef, CreateOrUpdateCustomerProps>(
  ({ onSuccess }, ref) => {
    const [data, setData] = useState<ICustomer | undefined>(undefined);
    const setLoadingPage = useSetLoadingPage();
    const [open, setOpen] = useState(false);
    const callbackOnSuccess = useRef<((data?: ICustomer) => void) | undefined>(undefined);

    const handleOnSuccess = (_data?: ICustomer) => {
      onSuccess?.(_data);
      callbackOnSuccess.current?.(_data);
    };

    const onClose = () => {
      setOpen(false);
      setData(undefined);
    };

    const onOpen = (data?: ICustomer) => {
      if (data) {
        setData(data);
      }
      setOpen(true);
    };

    const openWithId = async (id: string, configs?: { addon?: CustomerInfoPopover, onSuccess?: (data?: ICustomer) => void }) => {
      const { addon, onSuccess = () => undefined } = configs || {};
      callbackOnSuccess.current = onSuccess;
      // @ts-ignore
      onOpen({
        id: addon?.customerId || '',
        name: addon?.customerName || '',
        phone: addon?.customerPhone || '',
        note: addon?.customerNote || '',
        hobbies: addon?.customerHobbies || '',
        color: addon?.customerColor || '',
        health: addon?.customerHealth || '',
        accountPackage: addon?.accountPackage || IAccountPackage.NEW,
      });
      try {
        const res: IResponseDataBody<ICustomer> = await customerApis.getCustomerDetails(id);
        if (res.data.data) {
          setData({
            ...data || {},
            ...res.data.data,
          });
        }
      } catch (error) {
      }
    };

    useImperativeHandle(ref, () => ({
      onOpen,
      openWithId,
    }));

    return (
      <CreateOrUpdateCustomerModalStyled
        open={open}
        onCancel={onClose}
        title={data ? 'Edit Customer' : 'Create Customer'}
        footer={null}
        destroyOnClose={true}
        bodyStyle={{ padding: 0, margin: 0 }}
        closeIcon={null}
        centered
        width={'80vw'}
        closable={false}
      >
        <CreateOrUpdateCustomerForm data={data} onClose={onClose} onSuccess={handleOnSuccess} />
      </CreateOrUpdateCustomerModalStyled>
    );
  }
);

CreateOrUpdateCustomer.displayName = 'CreateOrUpdateCustomer';

export default CreateOrUpdateCustomer;

const CreateOrUpdateCustomerModalStyled = styled(Modal)`
  .ant-modal-content {
    opacity: 1;
    .ant-modal-header {
      background: #232f3e;
      .ant-modal-title {
        color: #fff;
        text-align: center;
        font-family: Poppins;
        font-size: 24px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
        text-transform: uppercase;
      }
    }
  }
`;
